import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import FontAwesomeIcon from 'src/components/Icon';
import Callout from 'src/components/InformationCallout/Callout';
import { useHorizontalScrollChevrons } from 'src/SharedCustomHooks/UseHorizontalScrollChevrons/useHorizontalScrollChevrons';
import { useNotesAPI } from 'src/SharedCustomHooks/UseNotesAPI/useNotesAPI';
import ServiceRequestDetails from 'src/pages/ServiceRequest/ServiceRequestDetails';
import ModernizedNotes from 'src/components/Notes/ModernizedNotes';
import ModernizedMessages from 'src/components/Messages/ModernizedMessages/ModernMessages';
import ModernizedServiceRequestDocuments from 'src/pages/MagicLinkSignedOutServiceRequest/components/ModernizedServiceRequestDocuments/ModernizedServiceRequestDocuments';
import './ModernizedServiceRequestTabs.scss';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  roles: { role: { name: string } }[];
}

interface ModernizedServiceRequestTabsProps {
  userId: string;
  saving: boolean;
  user: User;
  token: string;
  serviceRequest: any;
  documents: any[];
  messages: any[];
  saveDocument: (formData: any, srId: string, userId: string) => Promise<void>;
  postMessageToAPI: (chat: any) => Promise<void>;
}

const ModernizedServiceRequestTabs: React.FC<
  ModernizedServiceRequestTabsProps
> = ({
  userId,
  saving,
  user,
  token,
  serviceRequest,
  documents,
  messages,
  saveDocument,
  postMessageToAPI,
}) => {
  const [activeTab, setActiveTab] = useState(0);

  const tabsRef = useRef<HTMLDivElement>(null);
  const { showLeftChevron, showRightChevron } = useHorizontalScrollChevrons({
    scrollRef: tabsRef,
    dependencies: [],
  });

  const notesAPI = useNotesAPI(user, token);

  useEffect(() => {
    if (serviceRequest?.id) {
      void notesAPI.fetchNotes(serviceRequest.id);
    }
  }, [serviceRequest?.id]);

  const { notes } = notesAPI;

  const tabs = useMemo(
    () => [
      {
        label: 'Details',
        content: (
          <ServiceRequestDetails
            serviceRequest={serviceRequest}
            canEditAsset={false}
          />
        ),
      },
      {
        label: 'Messages',
        count: messages.length.toString(),
        content: (
          <ModernizedMessages
            messages={messages}
            saving={saving}
            offlineUser={user}
            onPostMessage={postMessageToAPI}
          />
        ),
      },
      {
        label: 'Photos & Documents',
        count: documents.length > 0 ? documents.length.toString() : undefined,
        content: (
          <ModernizedServiceRequestDocuments
            documents={documents}
            userId={userId}
            saving={saving}
            onAddDocument={(formData: any) =>
              saveDocument(formData, serviceRequest.id, userId)
            }
            user={user as any}
            canDelete={false}
          />
        ),
      },
      {

        label: 'Notes',
        count: notes.length.toString(),
        content: (
          <ModernizedNotes
            notesAPI={notesAPI}
            entityId={serviceRequest?.id ?? ''}
            itemType="Service Request"
            userId={userId}
            canAddNote={true}
            canDelete={false}
            loading={notesAPI.loadingNotes && notes.length === 0}
            addDialogDescription="Leave a status update or general comment for the team."
            editDialogDescription="Update your note below."
            showHomeownerVisibilityCheckbox
          />
        ),
      },
    ],
    [serviceRequest, notes, documents, messages, saving, userId, user, notesAPI]
  );

  return (
    <div className="osr-tabs">
      <div className="osr-tabs-mobile-hint">
        <Callout
          variant="info"
          title="Swipe the tab bar below left or right to navigate between sections."
        />
      </div>
      <div className="osr-tabs-nav">
        <div className="osr-tabs-items" ref={tabsRef}>
          {tabs.map((tab, i) => (
            <button
              key={i}
              className={classNames('osr-tabs-item', {
                'osr-tabs-item-active': i === activeTab,
              })}
              onClick={() => setActiveTab(i)}
            >
              {tab.label}
              {tab.count && <span className="osr-tabs-count">{tab.count}</span>}
            </button>
          ))}
        </div>
        <div
          className={classNames('osr-tabs-fade', 'osr-tabs-fade-left', {
            'osr-tabs-fade-hidden': !showLeftChevron,
          })}
        >
          <FontAwesomeIcon glyph="chevron-double-left" weight="solid" />
        </div>
        <div
          className={classNames('osr-tabs-fade', {
            'osr-tabs-fade-hidden': !showRightChevron,
          })}
        >
          <FontAwesomeIcon glyph="chevron-double-right" weight="solid" />
        </div>
      </div>
      <div className="osr-tabs-content">{tabs[activeTab].content}</div>
    </div>
  );
};

export default ModernizedServiceRequestTabs;
